import React, { useEffect, useMemo, useRef, useState } from "react";

import Modal from "@cloudscape-design/components/modal";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import FormField from "@cloudscape-design/components/form-field";
import Grid from "@cloudscape-design/components/grid";
import Input from "@cloudscape-design/components/input";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Alert from "@cloudscape-design/components/alert";

const MAX_PROMOCODES = 20;
const MAX_VISIBLE_GAMES = 100;

const channelOptions = [
	{ label: "Email", value: "Email" },
	{ label: "Site notification system", value: "SYS" },
	{ label: "Telegram", value: "TG" },
	{ label: "SMS", value: "SMS" },
];

const promoTypeOptions = [
	{ label: "Promocode", value: "code" },
	{ label: "Freespins", value: "fs" },
	{ label: "Mirror", value: "mirror" },
];

const initialForm = {
	channel: channelOptions[0],
	typePromo: promoTypeOptions[0],
	promocodes: [""],
	amount: "",
	wager: "",
	cap: "",
	fsCount: "",
	game: null,
	bet: "",
	periodDays: "",
};

const formatGameLabel = (name, gameID) => `${name || "Game"} #${gameID}`;

const buildGameOptions = (games = []) =>
	games
		.filter((game) => Number(game?.active) === 1 && game?.gameID)
		.slice()
		.sort((left, right) => {
			const leftName = String(left?.name || "").toLowerCase();
			const rightName = String(right?.name || "").toLowerCase();

			return leftName.localeCompare(rightName, undefined, { numeric: true, sensitivity: "base" }) || Number(left.gameID) - Number(right.gameID);
		})
		.map((game) => ({
			label: formatGameLabel(game.name, game.gameID),
			value: game.gameID.toString(),
			gameName: game.name || "Game",
			gameID: game.gameID,
		}));

function PlayerCallcenterTriggerModal({ visible, onDismiss, playerPage, game, Interface }) {
	const [form, setForm] = useState(initialForm);
	const [errors, setErrors] = useState({});
	const [submitError, setSubmitError] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [filteredGameOptions, setFilteredGameOptions] = useState([]);
	const wasVisible = useRef(false);
	const mounted = useRef(true);

	const gameOptions = useMemo(() => buildGameOptions(game?.data || []), [game?.data]);

	useEffect(() => {
		return () => {
			mounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (visible && !wasVisible.current) {
			setForm(initialForm);
			setErrors({});
			setSubmitError("");
			setSubmitting(false);
		}

		if (visible) {
			setFilteredGameOptions(gameOptions.slice(0, MAX_VISIBLE_GAMES));
		}

		wasVisible.current = visible;
	}, [gameOptions, visible]);

	const updateField = (field, value) => {
		setForm((currentForm) => ({
			...currentForm,
			[field]: value,
		}));
	};

	const updatePromocode = (index, value) => {
		setForm((currentForm) => {
			const promocodes = currentForm.promocodes.slice();
			promocodes[index] = value;

			return {
				...currentForm,
				promocodes,
			};
		});
	};

	const addPromocode = () => {
		setForm((currentForm) => ({
			...currentForm,
			promocodes: currentForm.promocodes.length >= MAX_PROMOCODES ? currentForm.promocodes : [...currentForm.promocodes, ""],
		}));
	};

	const removePromocode = (index) => {
		setForm((currentForm) => ({
			...currentForm,
			promocodes: currentForm.promocodes.filter((_, promocodeIndex) => promocodeIndex !== index),
		}));
	};

	const validate = () => {
		const nextErrors = {};
		const typePromo = form.typePromo?.value;

		if (!form.channel) nextErrors.channel = "Select channel";
		if (!form.typePromo) nextErrors.typePromo = "Select promo type";

		if (typePromo === "code") {
			if (!form.promocodes.some((promocode) => promocode.trim())) {
				nextErrors.promocodes = "Enter at least one promocode";
			}
			if (!form.amount) nextErrors.amount = "Enter amount";
			if (!form.wager) nextErrors.wager = "Enter wager";
			if (!form.cap) nextErrors.cap = "Enter cap";
		}

		if (typePromo === "fs") {
			if (!form.fsCount) nextErrors.fsCount = "Enter freespins count";
			if (!form.game) nextErrors.game = "Select game";
			if (!form.bet) nextErrors.bet = "Enter bet";
			if (!form.periodDays) nextErrors.periodDays = "Enter period days";
			if (!form.wager) nextErrors.wager = "Enter wager";
			if (!form.cap) nextErrors.cap = "Enter cap";
		}

		setErrors(nextErrors);

		return Object.keys(nextErrors).length === 0;
	};

	const buildPayload = () => {
		const payload = {
			channel: form.channel.value,
			type_promo: form.typePromo.value,
		};

		if (form.typePromo.value === "code") {
			form.promocodes
				.map((promocode) => promocode.trim())
				.filter(Boolean)
				.slice(0, MAX_PROMOCODES)
				.forEach((promocode, index) => {
					payload[`cc_promocode_${index + 1}`] = promocode;
				});

			payload.amount = form.amount;
			payload.wager = form.wager;
			payload.cap = form.cap;
		}

		if (form.typePromo.value === "fs") {
			payload.fs_count = form.fsCount;
			payload.game_name = form.game.gameName;
			payload.game_id = form.game.gameID;
			payload.bet = form.bet;
			payload.period_days = form.periodDays;
			payload.wager = form.wager;
			payload.cap = form.cap;
		}

		return payload;
	};

	const handleSendEvent = () => {
		if (!validate()) return;

		setSubmitError("");
		setSubmitting(true);
		Interface.sendCustomerCallcenterEvent(playerPage.uid, playerPage.data.project, buildPayload())
			.then(() => {
				if (mounted.current) onDismiss();
			})
			.catch((error) => {
				if (!mounted.current) return;
				setSubmitError(error?.text || error?.message || "Couldn't send customer callcenter event");
				setSubmitting(false);
			});
	};

	const handleGameSearch = (filteringText) => {
		const normalizedFilter = String(filteringText || "")
			.trim()
			.toLowerCase();
		const nextOptions = normalizedFilter.length === 0 ? gameOptions : gameOptions.filter((gameOption) => gameOption.label.toLowerCase().includes(normalizedFilter) || gameOption.value.includes(normalizedFilter));

		setFilteredGameOptions(nextOptions.slice(0, MAX_VISIBLE_GAMES));
	};

	const renderCodeFields = () => (
		<SpaceBetween direction="vertical" size="m">
			<FormField label="Promocodes" errorText={errors.promocodes}>
				<SpaceBetween direction="vertical" size="xs">
					{form.promocodes.map((promocode, index) => (
						<div style={{ display: "flex", gap: 10, alignItems: "center" }} key={index}>
							<div style={{ flex: 1 }}>
								<Input placeholder={`promocode ${index + 1}`} value={promocode} onChange={({ detail }) => updatePromocode(index, detail.value)} />
							</div>
							<Button iconName="remove" disabled={form.promocodes.length === 1} onClick={() => removePromocode(index)} />
						</div>
					))}
					<Button disabled={form.promocodes.length >= MAX_PROMOCODES} onClick={addPromocode}>
						Add promocode
					</Button>
				</SpaceBetween>
			</FormField>
			<Grid gridDefinition={[{ colspan: { default: 12, s: 4 } }, { colspan: { default: 12, s: 4 } }, { colspan: { default: 12, s: 4 } }]}>
				<FormField label="Amount" errorText={errors.amount}>
					<Input type="number" inputMode="numeric" placeholder="Amount" value={form.amount} onChange={({ detail }) => updateField("amount", detail.value)} />
				</FormField>
				<div style={{ display: "flex", gap: 10, alignItems: "center" }} direction="horizontal" size="xs">
					<div style={{ flex: 1 }}>
						<FormField label="Wager" errorText={errors.wager}>
							<Input type="number" inputMode="numeric" placeholder="Wager" value={form.wager} onChange={({ detail }) => updateField("wager", detail.value)} />
						</FormField>
					</div>
					<div style={{ flex: 1 }}>
						<FormField label="Cap" errorText={errors.cap}>
							<Input type="number" inputMode="numeric" placeholder="Cap" value={form.cap} onChange={({ detail }) => updateField("cap", detail.value)} />
						</FormField>
					</div>
				</div>
			</Grid>
		</SpaceBetween>
	);

	const renderFsFields = () => (
		<SpaceBetween direction="vertical" size="m">
			<Grid gridDefinition={[{ colspan: { default: 12, s: 6 } }, { colspan: { default: 12, s: 6 } }]}>
				<FormField label="Freespins count" errorText={errors.fsCount}>
					<Input type="number" inputMode="numeric" placeholder="Freespins count" value={form.fsCount} onChange={({ detail }) => updateField("fsCount", detail.value)} />
				</FormField>
				<FormField label="Game" errorText={errors.game}>
					<Select filteringType="manual" placeholder={game?.loaded ? "Select game" : "Loading games..."} selectedOption={form.game} onLoadItems={({ detail }) => handleGameSearch(detail.filteringText)} onChange={({ detail }) => updateField("game", detail.selectedOption)} options={filteredGameOptions} statusType={game?.loaded ? "finished" : "loading"} disabled={!game?.loaded || gameOptions.length === 0} />
				</FormField>
			</Grid>
			<Grid gridDefinition={[{ colspan: { default: 6, s: 2 } }, { colspan: { default: 6, s: 2 } }, { colspan: { default: 6, s: 2 } }, { colspan: { default: 6, s: 2 } }]}>
				<FormField label="Bet" errorText={errors.bet}>
					<Input type="number" inputMode="numeric" placeholder="Bet" value={form.bet} onChange={({ detail }) => updateField("bet", detail.value)} />
				</FormField>
				<FormField label="Period days" errorText={errors.periodDays}>
					<Input type="number" inputMode="numeric" placeholder="Period days" value={form.periodDays} onChange={({ detail }) => updateField("periodDays", detail.value)} />
				</FormField>
				<FormField label="Wager" errorText={errors.wager}>
					<Input type="number" inputMode="numeric" placeholder="Wager" value={form.wager} onChange={({ detail }) => updateField("wager", detail.value)} />
				</FormField>
				<FormField label="Cap" errorText={errors.cap}>
					<Input type="number" inputMode="numeric" placeholder="Cap" value={form.cap} onChange={({ detail }) => updateField("cap", detail.value)} />
				</FormField>
			</Grid>
		</SpaceBetween>
	);

	return (
		<Modal
			visible={visible}
			onDismiss={onDismiss}
			header="Callcenter trigger"
			size="medium"
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						<Button disabled={submitting} onClick={onDismiss}>
							Cancel
						</Button>
						<Button loading={submitting} variant="primary" onClick={handleSendEvent}>
							Send event
						</Button>
					</SpaceBetween>
				</Box>
			}>
			<SpaceBetween direction="vertical" size="m">
				{submitError && (
					<Alert type="error" dismissible onDismiss={() => setSubmitError("")}>
						{submitError}
					</Alert>
				)}
				<Grid gridDefinition={[{ colspan: { default: 12, s: 6 } }, { colspan: { default: 12, s: 6 } }]}>
					<FormField label="Channel" errorText={errors.channel}>
						<Select placeholder="Select channel" selectedOption={form.channel} onChange={({ detail }) => updateField("channel", detail.selectedOption)} options={channelOptions} />
					</FormField>
					<FormField label="Promo type" errorText={errors.typePromo}>
						<Select placeholder="Select promo type" selectedOption={form.typePromo} onChange={({ detail }) => updateField("typePromo", detail.selectedOption)} options={promoTypeOptions} />
					</FormField>
				</Grid>

				{form.typePromo?.value === "code" && renderCodeFields()}
				{form.typePromo?.value === "fs" && renderFsFields()}
				{form.typePromo?.value === "mirror" && <Box color="text-body-secondary">Mirror trigger does not require additional attributes.</Box>}
			</SpaceBetween>
		</Modal>
	);
}

export default PlayerCallcenterTriggerModal;
